import { CloseRound, RefreshLight } from '@repo/go-icons';
import { Button, Checkbox, Scrollbar } from '@repo/go-ui';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResizablePanel } from '../ResizablePanel/ResizablePanel';

import styles from './ContextSelectionPanel.module.css';

export type ContextOption = {
  label: string;
  value: string | number;
};

export type ContextValueType = 'number' | 'string';

export type ContextDirectory<TFieldName extends string> = {
  id: string;
  title: string;
  options: ContextOption[];
  multi?: boolean;
  fieldName: TFieldName;
  valueType?: ContextValueType;
};

export type ContextSelections = Record<string, (string | number)[]>;

type BaseContextSelectionPanelProps<TStore, TFieldName extends string> = {
  selectedRowIds: string[];
  store: TStore;
  directories: ContextDirectory<TFieldName>[];
  title?: string;
  onClose?: () => void;
  onResetAll?: () => void;
  className?: string;
  container: HTMLElement;
  getFieldValue: (store: TStore, rowId: string, fieldName: TFieldName) => unknown;
  applyFieldValue: (
    store: TStore,
    rowId: string,
    fieldName: TFieldName,
    values: (string | number)[],
    valueType: ContextValueType,
  ) => void;
};

export function BaseContextSelectionPanel<TStore, TFieldName extends string>({
  selectedRowIds,
  store,
  directories,
  title,
  onClose,
  onResetAll,
  className,
  container,
  getFieldValue,
  applyFieldValue,
}: BaseContextSelectionPanelProps<TStore, TFieldName>) {
  const { t } = useTranslation();
  const panelTitle = title ?? t('alertsManagerWidget:contextPanel.title');
  const initial = useMemo(
    () => getInitialSelections(store, selectedRowIds, directories, getFieldValue),
    [store, selectedRowIds, directories, getFieldValue],
  );
  const [value, setValue] = useState<ContextSelections>(initial);

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  const handleChange = useCallback(
    (next: ContextSelections) => {
      setValue(next);
      applySelectionsToRows(store, selectedRowIds, directories, next, applyFieldValue);
    },
    [store, selectedRowIds, directories, applyFieldValue],
  );

  const handleDirectorySelect = useCallback(
    (dirId: string, optionValue: string | number, multi: boolean) => {
      const current = value[dirId] ?? [];
      let next: (string | number)[];
      if (multi) {
        const has = current.includes(optionValue);
        next = has ? current.filter((v) => v !== optionValue) : [...current, optionValue];
      } else {
        const has = current.includes(optionValue);
        next = has ? [] : [optionValue];
      }
      handleChange({ ...value, [dirId]: next });
    },
    [value, handleChange],
  );

  const handleHeaderCheckboxClick = useCallback(
    (dir: ContextDirectory<TFieldName>) => {
      const current = value[dir.id] ?? [];
      const allValues = dir.options.map((o) => o.value);
      const allSelected = allValues.length > 0 && current.length >= allValues.length;
      if (dir.multi) {
        handleChange({ ...value, [dir.id]: allSelected ? [] : [...allValues] });
      } else {
        handleChange({ ...value, [dir.id]: [] });
      }
    },
    [value, handleChange],
  );

  const handleResetAll = useCallback(() => {
    const empty: ContextSelections = {};
    for (const dir of directories) {
      empty[dir.id] = [];
    }
    handleChange(empty);
    onResetAll?.();
  }, [directories, handleChange, onResetAll]);

  const columns = useMemo(() => {
    return directories.map((dir, index) => {
      const selected = value[dir.id] ?? [];
      const multi = dir.multi ?? false;
      const allValues = dir.options.map((o) => o.value);
      const allSelected = allValues.length > 0 && selected.length >= allValues.length;
      const someSelected = selected.length > 0;
      const isLast = index === directories.length - 1;

      return (
        <div key={dir.id} className={clsx(styles.column, isLast && styles.columnLast)}>
          <div className={styles.columnHeader}>
            <Checkbox
              checked={multi ? allSelected : someSelected}
              intermediate={multi && someSelected && !allSelected}
              onChange={() => handleHeaderCheckboxClick(dir)}
              className={styles.headerCheckbox}
              labelClassName={styles.columnTitle}
              aria-label={t(dir.title)}
            >
              {t(dir.title)}
            </Checkbox>
          </div>
          <Scrollbar>
            <div className={styles.optionList}>
              {dir.options.map((opt) => {
                const isSelected = selected.includes(opt.value);
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    title={opt.label}
                    className={clsx(styles.optionButton, isSelected && styles.optionButtonSelected)}
                    onClick={() => handleDirectorySelect(dir.id, opt.value, multi)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </Scrollbar>
        </div>
      );
    });
  }, [directories, value, handleDirectorySelect, handleHeaderCheckboxClick, t]);

  return (
    <ResizablePanel className={className} defaultHeightPercent={50} minHeightPx={250} container={container}>
      <div className={styles.panel}>
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>{panelTitle}</h2>
          <Button variant="flat" icon={<RefreshLight />} onClick={handleResetAll}>
            {t('alertsManagerWidget:resetAll')}
          </Button>
          {onClose && (
            <button type="button" className={styles.closeButton} onClick={onClose}>
              <CloseRound />
            </button>
          )}
        </div>
        <div className={styles.content}>{columns}</div>
      </div>
    </ResizablePanel>
  );
}

function normalizeArray(raw: unknown, valueType: ContextValueType): (number | string)[] {
  const arr = Array.isArray(raw) ? raw : [];
  if (valueType === 'string') {
    return arr.map((v) => String(v));
  }
  return arr.map((v) => (typeof v === 'number' ? v : Number(v))).filter((n) => !Number.isNaN(n));
}

function arraysEqual(a: (number | string)[], b: (number | string)[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  return sa.every((v, i) => v === sb[i]);
}

function getInitialSelections<TStore, TFieldName extends string>(
  store: TStore,
  selectedRowIds: string[],
  directories: ContextDirectory<TFieldName>[],
  getFieldValue: (store: TStore, rowId: string, fieldName: TFieldName) => unknown,
): ContextSelections {
  if (selectedRowIds.length === 0) return {};
  const result: ContextSelections = {};
  for (const dir of directories) {
    const valueType = dir.valueType ?? 'number';
    const first = normalizeArray(getFieldValue(store, selectedRowIds[0], dir.fieldName), valueType);
    const allSame =
      selectedRowIds.length === 1 ||
      selectedRowIds.every((rowId) => {
        const arr = normalizeArray(getFieldValue(store, rowId, dir.fieldName), valueType);
        return arraysEqual(first, arr);
      });
    result[dir.id] = allSame ? first : [];
  }
  return result;
}

function applySelectionsToRows<TStore, TFieldName extends string>(
  store: TStore,
  selectedRowIds: string[],
  directories: ContextDirectory<TFieldName>[],
  selections: ContextSelections,
  applyFieldValue: (
    store: TStore,
    rowId: string,
    fieldName: TFieldName,
    values: (string | number)[],
    valueType: ContextValueType,
  ) => void,
): void {
  for (const rowId of selectedRowIds) {
    for (const dir of directories) {
      const values = selections[dir.id] ?? [];
      applyFieldValue(store, rowId, dir.fieldName, values, dir.valueType ?? 'number');
    }
  }
}
